/**
 * OM Remote 頁面配置
 *
 * 定義所有 om 模組的頁面、Drawer 和服務，自動生成：
 * - vite.config exposes
 * - remoteModules
 * - pagePermissions
 * - sideNavMenus
 */

import type { RemoteModuleConfig, PageConfig, DrawerConfig } from './types';

// ============================================================================
// 頁面配置
// ============================================================================

export const pages: PageConfig[] = [
    // ===== operation 模式 - 巡檢 =====
    {
        name: 'FocusField',
        path: './src/views/operations/patrol/FocusField.vue',
        title: '焦點案場',
        mode: 'operation',
        icon: 'icofont-star',
        default: true,
        showInSideNav: true,
        standalone: { show: true, group: '巡檢', order: 1 },
    },
    {
        name: 'SystemOverview',
        path: './src/views/operations/patrol/SystemOverview.vue',
        title: '系統看板',
        mode: 'operation',
        icon: 'icofont-dashboard-web',
        default: true,
        showInSideNav: true,
        standalone: { show: true, group: '巡檢', order: 2 },
    },
    {
        name: 'PVFieldStatus',
        path: './src/views/operations/patrol/PVFieldStatus.vue',
        title: '光電案場狀況',
        mode: 'operation',
        default: true,
        standalone: { show: true, group: '案場狀況', order: 1 },
    },
    {
        name: 'ESFieldStatus',
        path: './src/views/operations/patrol/ESFieldStatus.vue',
        title: '儲能案場狀況',
        mode: 'operation',
        default: true,
        standalone: { show: true, group: '案場狀況', order: 2 },
    },
    {
        name: 'BGFieldStatus',
        path: './src/views/operations/patrol/BGFieldStatus.vue',
        title: '生質能案場狀況',
        mode: 'operation',
        default: true,
        standalone: { show: true, group: '案場狀況', order: 3 },
    },
    {
        name: 'HPFieldStatus',
        path: './src/views/operations/patrol/HPFieldStatus.vue',
        title: '熱泵案場狀況',
        mode: 'operation',
        default: true,
        standalone: { show: true, group: '案場狀況', order: 4 },
    },
    {
        name: 'VPPFieldStatus',
        path: './src/views/operations/patrol/VPPFieldStatus.vue',
        title: '複合案場狀況',
        mode: 'operation',
        default: true,
        standalone: { show: true, group: '案場狀況', order: 5 },
    },

    // ===== operation 模式 - 管理 =====
    {
        name: 'EquipmentOverview',
        path: './src/views/operations/manage/EquipmentOverview.vue',
        title: '設備總覽',
        mode: 'operation',
        icon: 'icofont-listing-box',
        default: true,
        showInSideNav: true,
        standalone: { show: true, group: '管理', order: 1 },
    },
    {
        name: 'EventsList',
        path: './src/views/operations/manage/EventsList.vue',
        title: '訊息匯整',
        mode: 'operation',
        icon: 'icofont-email',
        default: true,
        showInSideNav: true,
        standalone: { show: true, group: '管理', order: 2 },
    },
    {
        name: 'FieldSelector',
        path: './src/views/operations/manage/FieldSelector.vue',
        title: '案場選擇',
        mode: 'operation',
        icon: 'icofont-location-pin',
        default: true,
        showInSideNav: true,
    },

    // ===== operation 模式 - 派工 =====
    {
        name: 'WorkOrderList',
        path: './src/views/operations/dispatch/WorkOrderList.vue',
        title: '工單列表',
        mode: 'operation',
        icon: 'icofont-repair',
        default: true,
        showInSideNav: true,
        standalone: { show: true, group: '派工', order: 1 },
    },
    {
        name: 'WorkOrderDetail',
        path: './src/views/operations/dispatch/WorkOrderDetail.vue',
        title: '工單詳情',
        mode: 'operation',
        default: true,
    },
    {
        name: 'DispatchCalendar',
        path: './src/views/operations/dispatch/DispatchCalendar.vue',
        title: '派工行事曆',
        mode: 'operation',
        default: true,
        standalone: { show: true, group: '派工', order: 2 },
    },
    {
        name: 'WorkReport',
        path: './src/views/operations/dispatch/WorkReport.vue',
        title: '工作回報',
        mode: 'operation',
        default: true,
        standalone: { show: true, group: '派工', order: 3 },
    },
    {
        name: 'MaintenanceBoard',
        path: './src/views/operations/dispatch/MaintenanceBoard.vue',
        title: '維運看板',
        mode: 'operation',
        icon: 'icofont-tasks-alt',
        default: true,
        showInSideNav: true,
        standalone: { show: true, group: '派工', order: 4 },
    },

    // ===== operation 模式 - 告警 =====
    {
        name: 'AlarmList',
        path: './src/views/operations/alarm/AlarmList.vue',
        title: '告警列表',
        mode: 'operation',
        icon: 'icofont-alarm',
        default: true,
        showInSideNav: true,
        standalone: { show: true, group: '管理', order: 3 },
    },

    // ===== warroom 模式 - 戰情室 =====
    {
        name: 'VisualizationDashboard',
        path: './src/views/warroom/VisualizationDashboard.vue',
        title: '戰情看板',
        mode: 'warroom',
        icon: 'icofont-chart-pie',
        default: true,
        showInSideNav: true,
        standalone: { show: true, group: '戰情', order: 1 },
    },
    {
        name: 'RiskDashboard',
        path: './src/views/warroom/RiskDashboard.vue',
        title: '風險看板',
        mode: 'warroom',
        icon: 'icofont-warning',
        default: true,
        showInSideNav: true,
        standalone: { show: true, group: '戰情', order: 2 },
    },
    {
        name: 'EventsDashboard',
        path: './src/views/warroom/EventsDashboard.vue',
        title: '事件看板',
        mode: 'warroom',
        icon: 'icofont-bell',
        default: true,
        showInSideNav: true,
        standalone: { show: true, group: '戰情', order: 3 },
    },
    {
        name: 'EmergencyDashboard',
        path: './src/views/warroom/EmergencyDashboard.vue',
        title: '應急響應',
        mode: 'warroom',
        icon: 'icofont-fire-alt',
        default: true,
        showInSideNav: true,
        standalone: { show: true, group: '戰情', order: 4 },
    },
    {
        name: 'FinancialDashboard',
        path: './src/views/warroom/FinancialDashboard.vue',
        title: '收支看板',
        mode: 'warroom',
        icon: 'icofont-money',
        default: true,
        showInSideNav: true,
        standalone: { show: true, group: '戰情', order: 5 },
    },

    // ===== report 模式 - 報表 =====
    {
        name: 'EnergyStatistics',
        path: './src/views/operations/report/EnergyStatistics.vue',
        title: '數據趨勢',
        mode: 'report',
        icon: 'icofont-chart-line',
        default: true,
        showInSideNav: true,
        standalone: { show: true, group: '報表', order: 1 },
    },
    {
        name: 'EnergyGeneration',
        path: './src/views/operations/report/EnergyGeneration.vue',
        title: '發電統計',
        mode: 'report',
        icon: 'icofont-chart-bar-graph',
        default: true,
        showInSideNav: true,
        standalone: { show: true, group: '報表', order: 2 },
    },
    {
        name: 'ReportList',
        path: './src/views/operations/report/ReportList.vue',
        title: '報表管理',
        mode: 'report',
        icon: 'icofont-file-text',
        default: true,
        showInSideNav: true,
        standalone: { show: true, group: '報表', order: 3 },
    },
    {
        name: 'BillList',
        path: './src/views/operations/report/BillList.vue',
        title: '電費帳單',
        mode: 'report',
        icon: 'icofont-ticket',
        default: true,
        showInSideNav: true,
        standalone: { show: true, group: '報表', order: 4 },
    },
    {
        name: 'BillDetail',
        path: './src/views/operations/report/BillDetail.vue',
        title: '電費明細',
        mode: 'report',
        default: true,
    },
    // MaintenanceFinanceList / MaintenanceFinanceDetail 已移至 core/src/views/report/（本地載入，支援 hot-reload）

    // ===== field 模式 - 案場頁面 =====
    // FieldFinance 已移至 core/src/views/field/FieldFinance.vue（本地載入）
    {
        name: 'FieldRiskTrend',
        path: './src/views/operations/field/FieldRiskTrend.vue',
        title: '案場風險趨勢',
        mode: 'field',
        default: true,
    },

    // ===== org 模式 - 組織管理 =====
    {
        name: 'OrganizationDetail',
        path: './src/views/operations/manage/OrganizationDetail.vue',
        title: '組織設定',
        mode: 'org',
        icon: 'icofont-building-alt',
        default: true,
        showInSideNav: true,
        standalone: { show: true, group: '組織', order: 1 },
    },
    {
        name: 'OrganizationMember',
        path: './src/views/operations/manage/OrganizationMember.vue',
        title: '成員管理',
        mode: 'org',
        icon: 'icofont-users-alt-2',
        default: true,
        showInSideNav: true,
        standalone: { show: true, group: '組織', order: 2 },
    },
    {
        name: 'OrganizationInvite',
        path: './src/views/operations/manage/OrganizationInvite.vue',
        title: '邀請管理',
        mode: 'org',
        icon: 'icofont-envelope',
        default: true,
        showInSideNav: true,
        standalone: { show: true, group: '組織', order: 3 },
    },
    {
        name: 'OrganizationField',
        path: './src/views/operations/manage/OrganizationField.vue',
        title: '組織案場',
        mode: 'org',
        icon: 'icofont-building',
        default: true,
        showInSideNav: true,
        standalone: { show: true, group: '組織', order: 4 },
    },
    {
        name: 'OrganizationJoin',
        path: './src/views/operations/manage/OrganizationJoin.vue',
        title: '加入組織',
        mode: 'org',
        icon: 'icofont-login',
        default: true,
        showInSideNav: true,
    },

    // ===== quotation 模式 - 報價相關 =====
    {
        name: 'CustomerManagement',
        path: './src/views/planner/CustomerManagement.vue',
        title: '客戶管理',
        mode: 'quotation',
        default: false,
    },
    {
        name: 'QuotationList',
        path: './src/views/planner/QuotationList.vue',
        title: 'OM 報價列表',
        mode: 'quotation',
        default: false,
    },
    {
        name: 'QuotationHome',
        path: './src/views/planner/QuotationHome.vue',
        title: '報價工具',
        mode: 'quotation',
        default: false,
    },
    {
        name: 'ProjectManagement',
        path: './src/views/planner/ProjectManagement.vue',
        title: '專案管理',
        mode: 'quotation',
        default: false,
    },

    // ===== ess 模式 - 儲能控制 =====
    {
        name: 'ESSControl',
        path: './src/views/operations/ess/ESSControl.vue',
        title: '儲能控制',
        mode: 'ess',
        icon: 'icofont-ui-power',
        default: false,
        showInSideNav: true,
    },
    {
        name: 'ESSMonitor',
        path: './src/views/operations/ess/ESSMonitor.vue',
        title: '儲能監控',
        mode: 'ess',
        icon: 'icofont-chart-histogram',
        default: false,
        showInSideNav: true,
    },
];

// ============================================================================
// Drawer 配置
// ============================================================================

export const drawers: DrawerConfig[] = [
    { name: 'EquipmentDetail', path: './src/views/operations/drawer/EquipmentDetail.vue', title: '設備詳情頁' },
    { name: 'Chart', path: './src/views/operations/drawer/Chart.vue', title: '圖表' },
    { name: 'EventDetail', path: './src/views/operations/drawer/EventDetail.vue', title: '事件詳情' },
    { name: 'RiskEventDetail', path: './src/views/operations/drawer/RiskEventDetail.vue', title: '風險事件詳情' },
    { name: 'FieldDetail', path: './src/views/operations/drawer/FieldDetail.vue', title: '案場編輯' },
    { name: 'FieldEventCreate', path: './src/views/operations/drawer/FieldEventCreate.vue', title: '履歷新增' },
    { name: 'MemberDetail', path: './src/views/operations/drawer/MemberDetail.vue', title: '成員編輯' },
    { name: 'SystemDataEdit', path: './src/views/operations/drawer/SystemDataEdit.vue', title: '系統編輯' },
    { name: 'SystemEquipmentMapping', path: './src/views/operations/drawer/SystemEquipmentMapping.vue', title: '系統設備管理' },
    { name: 'BillCreate', path: './src/views/operations/drawer/BillCreate.vue', title: '建立電費帳單' },
    { name: 'WorkOrderEdit', path: './src/views/operations/drawer/WorkOrderEdit.vue', title: '工單編輯' },
    { name: 'DocumentEdit', path: './src/views/operations/drawer/DocumentEdit.vue', title: '文件編輯' },
    { name: 'ExportDrawer', path: './src/views/operations/drawer/ExportDrawer.vue', title: '匯出工單' },
    { name: 'GeneratorDetail', path: './src/views/operations/drawer/GeneratorDetail.vue', title: '發電機詳情' },
    { name: 'InverterDetail', path: './src/views/operations/drawer/InverterDetail.vue', title: '逆變器詳情' },
    { name: 'WarrantyDetail', path: './src/views/operations/drawer/WarrantyDetail.vue', title: '保固詳情' },
    { name: 'CreateMaintainRecord', path: './src/views/operations/drawer/CreateMaintainRecord.vue', title: '保養記錄' },
    { name: 'AIPromptHistory', path: './src/views/operations/drawer/AIPromptHistory.vue', title: 'AI 執行歷史' },
    { name: 'RepairOrderAssistant', path: './src/views/operations/drawer/RepairOrderAssistant.vue', title: 'AI 建立報修單' },
    { name: 'CollectedInfo', path: './src/views/operations/drawer/CollectedInfo.vue', title: '收集到的資訊' },
    { name: 'caseMaintenance', path: './src/views/operations/drawer/caseMaintenance.vue', title: '案件保養詳細' },
    { name: 'UserUIEditor', path: './src/views/operations/drawer/UserUIEditor.vue', title: 'User UI 編輯' },
    { name: 'UserUIPreview', path: './src/views/operations/drawer/UserUIPreview.vue', title: 'User UI 預覽' },
];

// ============================================================================
// 服務配置
// ============================================================================

export const services = [
    { name: 'eventService', path: './src/services/event/index.ts' },
    { name: 'fieldService', path: './src/services/field/fieldService.ts' },
    { name: 'equipmentService', path: './src/services/equipment/index.ts' },
];

// ============================================================================
// 模組完整配置
// ============================================================================

export const config: RemoteModuleConfig = {
    name: 'om',
    federationName: 'om_remote',
    port: 7001,
    pages,
    drawers,
    services,
};

export default config;
